import { getSlopeBounds } from './SlopeConfig';

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Time between path waypoints (seconds).
 * Increasing this from 0.1 → 0.25 means waypoints are 2.5× further apart in
 * time, so the interpolated path changes direction far less frequently and the
 * skier reads as making deliberate sweeping turns rather than twitching.
 */
const WAYPOINT_DT    = 0.25;
const WAYPOINT_COUNT = 1200;  // covers 300 s of play at WAYPOINT_DT=0.25

const SPAWN_SPEC_COUNT = 2000;

export interface PathWaypoint {
  t:     number;
  normX: number;
}

export interface SpawnSpec {
  isTree:        boolean;
  scale:         number;
  normX:         number;
  intervalTicks: number;
}

export function deriveDomainSeed(serverSeedHash: string, domain: string): number {
  const hashPart = parseInt((serverSeedHash || '00000000').slice(0, 8), 16) || 0x12345678;
  let domPart = 0x5a5a5a5a;
  for (let i = 0; i < domain.length; i++) {
    domPart = (Math.imul(domPart, 31) + domain.charCodeAt(i)) | 0;
  }
  return (hashPart ^ domPart ^ 0xdeadbeef) >>> 0;
}

export class SeededLanePath {
  private waypoints: PathWaypoint[];

  constructor(seed: number, laneIndex: number, totalLanes: number) {
    const rand  = mulberry32(seed);
    const homeX = (laneIndex + 0.5) / totalLanes;

    const pts: PathWaypoint[] = [];
    let x   = homeX;

    /**
     * Velocity is initialised small so the skier starts heading roughly
     * straight down with at most a gentle lean.
     */
    let vel = (rand() - 0.5) * 0.015;

    for (let i = 0; i < WAYPOINT_COUNT; i++) {
      /**
       * Home-lane pull: always draw back toward the assigned lane centre.
       * Strength 0.055 (was 0.038) keeps lanes distinct even under large jitter.
       */
      const pull = (homeX - x) * 0.055;

      /**
       * Jitter reduced from 0.072 → 0.022.
       * This is the single biggest source of visual noise: the old value
       * produced ±3.6% of slope width per waypoint, meaning the skier could
       * swing ~20 px sideways every 0.1 s.  The new value keeps micro-inputs
       * subtle so only the pull and accumulated momentum drive direction.
       */
      const jitter = (rand() - 0.5) * 0.022;

      /**
       * Velocity damping increased from 0.78 → 0.88.
       * Higher damping means velocity bleeds off more slowly, creating longer
       * sweeping arcs instead of rapid reversals.
       */
      vel = vel * 0.88 + pull + jitter;

      /**
       * Clamp velocity so individual steps stay bounded.
       * ±0.012 of normalised width ≈ ±7 px per waypoint on a 600 px slope.
       */
      vel = Math.max(-0.012, Math.min(0.012, vel));

      x = Math.max(0.04, Math.min(0.96, x + vel));
      pts.push({ t: i * WAYPOINT_DT, normX: x });
    }

    this.waypoints = pts;
  }

  /**
   * Get the normalised X position on the path at time `t` (seconds).
   * Uses linear interpolation between the two surrounding waypoints.
   */
  getNormX(t: number): number {
    const raw  = Math.max(0, t / WAYPOINT_DT);
    const lo   = Math.min(Math.floor(raw), this.waypoints.length - 2);
    const frac = raw - lo;
    const a    = this.waypoints[lo].normX;
    const b    = this.waypoints[lo + 1].normX;
    return a + (b - a) * Math.max(0, Math.min(1, frac));
  }

  toCanvasX(normX: number, canvasWidth: number, spriteWidth: number): number {
    const { left, right } = getSlopeBounds(canvasWidth);
    return left + normX * (right - left - spriteWidth);
  }
}

export function generateObstacleSpecs(serverSeedHash: string): SpawnSpec[] {
  const seed = deriveDomainSeed(serverSeedHash, 'obstacles');
  const rand = mulberry32(seed);
  const specs: SpawnSpec[] = [];

  const BASE_INTERVAL    = 90;
  const INTERVAL_VARIANCE = 0.25;

  for (let i = 0; i < SPAWN_SPEC_COUNT; i++) {
    const isTree        = rand() > 0.30;
    const scale         = 1.20 + rand() * 0.40;
    const normX         = rand();
    const intervalTicks = Math.round(
      BASE_INTERVAL * (1 - INTERVAL_VARIANCE + rand() * INTERVAL_VARIANCE * 2),
    );
    specs.push({ isTree, scale, normX, intervalTicks });
  }

  return specs;
}